import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { openConsent } from "./Consent";
import { Mail, Facebook, Instagram, Youtube, Linkedin, Twitter, Globe, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { useCategories } from "../../lib/hooks";
import { BRAND } from "../../lib/brand";
import { api } from "../../lib/api";

const ICONS = { facebook: Facebook, instagram: Instagram, youtube: Youtube, linkedin: Linkedin, x: Twitter, tiktok: Globe, whatsapp: MessageCircle };
let footerCache = null;

const useFooter = () => {
  const [cfg, setCfg] = useState(footerCache);
  useEffect(() => { api.get("/public/footer").then((r) => { footerCache = r.data; setCfg(r.data); }).catch(() => {}); }, []);
  return cfg;
};

const Col = ({ title, children }) => (
  <div>
    <h2 className="mb-3 text-[11px] font-extrabold uppercase tracking-[0.14em] text-muted-foreground">{title}</h2>
    <ul className="space-y-2 text-[14px]">{children}</ul>
  </div>
);

const FLink = ({ to, children, lang }) => (
  <li><Link to={to} lang={lang} className="text-foreground/85 transition-colors hover:text-pulse focus-visible:text-pulse" data-testid={`footer-link-${to.replace(/[/?=]/g, "") || "home"}`}>{children}</Link></li>
);

export const FooterColumns = ({ cfg, cats }) => (
  <div className="grid grid-cols-2 gap-8 sm:grid-cols-4 md:col-span-8" data-testid="footer-columns">
    {cfg.sections.map((s) => (
      <Col key={s.id} title={s.title}>
        {s.links.map((l, i) => <FLink key={`${l.to}-${i}`} to={l.to} lang={l.lang || undefined}>{l.label}</FLink>)}
        {s.type === "categories" && cats.filter((c) => c.slug !== "vodici" && (!s.category_slugs?.length || s.category_slugs.includes(c.slug)))
          .map((c) => <FLink key={c.slug} to={`/${c.slug}`}>{c.name}</FLink>)}
      </Col>
    ))}
  </div>
);

export const SiteFooter = () => {
  const cats = useCategories();
  const cfg = useFooter();
  return (
    <footer className="mt-16 border-t-[3px] border-navy bg-surface dark:border-pulse" data-testid="site-footer">
      <div className="container-news grid gap-10 py-12 md:grid-cols-12">
        <div className="md:col-span-4">
          <Link to="/" aria-label="BalkanPuls.de početna" data-testid="footer-logo-link"><Logo className="h-14" testId="footer-logo" /></Link>
          <p className="mt-4 max-w-sm text-[14px] leading-relaxed text-muted-foreground">
            Digitalni portal s vijestima, vodičima i informacijama za ljude iz Bosne i Hercegovine, Srbije, Hrvatske, Crne Gore, Kosova i Sjeverne Makedonije u Njemačkoj.
          </p>
          <a href={`mailto:${BRAND.email}`} className="mt-4 inline-flex items-center gap-2 text-[14px] font-bold text-ink hover:text-pulse" data-testid="footer-contact-email">
            <Mail className="h-4 w-4" /> {BRAND.email}
          </a>
          {cfg?.social?.length > 0 && <div className="mt-4 flex gap-3" data-testid="footer-social">{cfg.social.map((s) => { const I = ICONS[s.network] || Globe; return <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.network} className="rounded-full border border-rule p-2 text-ink transition-colors hover:border-pulse hover:text-pulse" data-testid={`footer-social-${s.network}`}><I className="h-4 w-4" /></a>; })}</div>}
        </div>
        {cfg ? <FooterColumns cfg={cfg} cats={cats} /> : (
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-4 md:col-span-8">
          <Col title="Rubrike">
            <FLink to="/vijesti">Vijesti</FLink>
            {cats.filter((c) => c.slug !== "vodici").map((c) => <FLink key={c.slug} to={`/${c.slug}`}>{c.name}</FLink>)}
          </Col>
          <Col title="Vodiči i alati">
            <FLink to="/vodici">Vodiči</FLink>
            <FLink to="/vodici?tema=penzije">Penzije i staž</FLink>
            <FLink to="/formulari">Službeni formulari</FLink>
            <FLink to="/pitaj-balkan-ai">Pitaj Balkan AI</FLink>
            <FLink to="/alati">Kalkulatori</FLink>
            <FLink to="/premium">Premium</FLink>
          </Col>
          <Col title="BalkanPuls">
            <FLink to="/o-nama">O nama</FLink>
            <FLink to="/kontakt">Kontakt</FLink>
            <FLink to="/najnovije">Najnovije</FLink>
            <FLink to="/pretraga">Pretraga</FLink>
            <FLink to="/partneri">Partneri i usluge</FLink>
            <FLink to="/postani-partner">Postani partner</FLink>
            <FLink to="/oglasavanje">Oglašavanje</FLink>
          </Col>
          <Col title="Redakcija i pravno">
            <FLink to="/redaktionelle-richtlinien">Urednička pravila</FLink>
            <FLink to="/korrekturen">Ispravke</FLink>
            <FLink to="/ki-transparenz">AI transparentnost</FLink>
            <FLink to="/impressum" lang="de">Impressum</FLink>
            <FLink to="/datenschutz" lang="de">Datenschutz</FLink>
            <FLink to="/agb" lang="de">AGB</FLink>
            <FLink to="/nutzungsbedingungen" lang="de">Nutzungsbedingungen</FLink>
            <FLink to="/premium-bedingungen" lang="de">Premium-Bedingungen</FLink>
            <FLink to="/werberichtlinien" lang="de">Werberichtlinien</FLink>
            <FLink to="/cookie-richtlinie" lang="de">Cookie-Richtlinie</FLink>
            <FLink to="/widerruf" lang="de">Widerruf</FLink>
            <FLink to="/kuendigung" lang="de">Kündigung</FLink>
            <FLink to="/urheberrecht" lang="de">Urheberrecht</FLink>
          </Col>
        </div>)}
      </div>
      <div className="border-t border-rule">
        <div className="container-news flex flex-col gap-1 py-4 text-[12px] text-muted-foreground sm:flex-row sm:justify-between">
          <span>© {new Date().getFullYear()} BalkanPuls.de · {BRAND.slogan}</span>
          <button type="button" onClick={openConsent} className="text-left underline-offset-2 hover:text-pulse hover:underline" data-testid="footer-cookie-settings">Postavke kolačića</button>
        </div>
      </div>
    </footer>
  );
};
